<script setup>
import { computed, ref } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  showUnpin: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['unpin', 'jumpToMessage']);

const isCollapsed = ref(true);
const MAX_PREVIEW_LENGTH = 80;

const latestMessage = computed(() => {
  if (!props.messages.length) return null;
  return props.messages[props.messages.length - 1];
});

const remainingCount = computed(() => {
  return Math.max(0, props.messages.length - 1);
});

const hasMoreMessages = computed(() => {
  return props.messages.length > 1;
});

const getMessagePreview = message => {
  if (!message.content) return '';
  return message.content.length > MAX_PREVIEW_LENGTH
    ? message.content.substring(0, MAX_PREVIEW_LENGTH) + '...'
    : message.content;
};

// Get sender name
const getSenderName = message => {
  if (!message.sender) return '';
  return message.sender.name || message.sender.email || 'Unknown';
};

// Jump to specific message
const jumpToMessage = message => {
  emit('jumpToMessage', message);
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: message.id,
  });
};

// Toggle collapse state
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};

// Handle unpin
const handleUnpin = message => {
  emit('unpin', message);
};
</script>

<template>
  <div v-if="messages.length" class="pinned-smart">
    <!-- Smart Header -->
    <div class="smart-header">
      <div class="header-left">
        <FluentIcon icon="pin" size="16" class="pin-icon" />
        <span class="count">{{ messages.length }}</span>
        <span class="title">{{ $t('CONVERSATION.PINNED_MESSAGES.TITLE', { count: messages.length }) }}</span>
      </div>
      <button
        v-if="hasMoreMessages"
        class="toggle-btn"
        @click="toggleCollapse"
      >
        <FluentIcon :icon="isCollapsed ? 'chevron-down' : 'chevron-up'" size="16" />
      </button>
    </div>

    <!-- Smart Collapsed View -->
    <div v-if="isCollapsed && latestMessage" class="smart-preview">
      <div class="preview-content">
        <p class="message-text">{{ getMessagePreview(latestMessage) }}</p>
        <div class="preview-meta">
          <span class="sender">{{ getSenderName(latestMessage) }}</span>
          <div class="actions">
            <button class="action-link" @click="jumpToMessage(latestMessage)">
              {{ $t('CONVERSATION.PINNED_MESSAGES.JUMP_TO_MESSAGE') }}
            </button>
            <button v-if="showUnpin" class="action-link unpin" @click="handleUnpin(latestMessage)">
              {{ $t('CONVERSATION.PINNED_MESSAGES.UNPIN') }}
            </button>
          </div>
        </div>
      </div>
      
      <!-- Smart More Indicator -->
      <div v-if="hasMoreMessages" class="more-smart">
        <span>{{ $t('CONVERSATION.PINNED_MESSAGES.MORE_MESSAGES', { count: remainingCount }) }}</span>
      </div>
    </div>

    <!-- Smart Expanded View -->
    <div v-else class="smart-list">
      <div
        v-for="(message, index) in messages"
        :key="message.id"
        class="smart-item"
      >
        <div class="item-content">
          <p class="item-text">{{ message.content }}</p>
          <div class="item-meta">
            <span class="item-sender">{{ getSenderName(message) }}</span>
            <div class="item-actions">
              <button class="action-link" @click="jumpToMessage(message)">
                {{ $t('CONVERSATION.PINNED_MESSAGES.JUMP_TO_MESSAGE') }}
              </button>
              <button v-if="showUnpin" class="action-link unpin" @click="handleUnpin(message)">
                {{ $t('CONVERSATION.PINNED_MESSAGES.UNPIN') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pinned-smart {
  @apply mb-4;
}

/* Smart Header */
.smart-header {
  @apply flex items-center justify-between mb-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700;
}

.header-left {
  @apply flex items-center gap-2;
}

.pin-icon {
  @apply text-woot-500;
}

.count {
  @apply text-sm font-semibold text-woot-500 bg-woot-100 dark:bg-woot-900/30 px-2 py-0.5 rounded;
}

.title {
  @apply text-sm font-medium text-slate-700 dark:text-slate-300;
}

.toggle-btn {
  @apply p-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors;
}

/* Smart Preview */
.smart-preview {
  @apply bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700;
}

.preview-content {
  @apply p-3;
}

.message-text {
  @apply text-sm text-slate-800 dark:text-slate-200 mb-2 leading-relaxed;
}

.preview-meta {
  @apply flex items-center justify-between;
}

.sender {
  @apply text-xs text-slate-600 dark:text-slate-400;
}

.actions {
  @apply flex gap-3;
}

.action-link {
  @apply text-xs text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 transition-colors;
}

.action-link.unpin {
  @apply text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300;
}

.more-smart {
  @apply px-3 py-2 bg-slate-50 dark:bg-slate-800 text-xs text-slate-600 dark:text-slate-400 border-t border-slate-200 dark:border-slate-700;
}

/* Smart List */
.smart-list {
  @apply space-y-2;
}

.smart-item {
  @apply bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700;
}

.item-content {
  @apply p-3;
}

.item-text {
  @apply text-sm text-slate-800 dark:text-slate-200 mb-2 leading-relaxed;
}

.item-meta {
  @apply flex items-center justify-between;
}

.item-sender {
  @apply text-xs text-slate-600 dark:text-slate-400;
}

.item-actions {
  @apply flex gap-3;
}

/* Responsive */
@media (max-width: 640px) {
  .preview-meta,
  .item-meta {
    @apply flex-col items-start gap-2;
  }
  
  .actions,
  .item-actions {
    @apply flex-col gap-1;
  }
  
  .message-text,
  .item-text {
    @apply line-clamp-2;
  }
}

/* Simple animations */
.pinned-smart {
  @apply transition-all duration-200;
}

.smart-preview,
.smart-item {
  @apply transition-all duration-200;
}

.smart-preview:hover,
.smart-item:hover {
  @apply shadow-sm;
}
</style>
